import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ImageViewer from "../../../components/imageViewer";
import { store } from "../../../redux/store";
import { showEnquireModal } from "../../../connected-components/enquireModal/reducer";
import { useLandDetail } from "../../../shared/hooks/useLandDetail";
import { formatPrice } from "../../../shared/utils/helper";
import LandDetailsSkeleton from "./components/landDetailsSkeleton";
import generateMeta from "../../../shared/services/metadataService";
import metaData from "../../../config/metaData";
import mapboxgl from "mapbox-gl";
import environment from "../../../environment/environment";

mapboxgl.accessToken = environment.mapboxToken;

// Map component to display land location
const LocationMap = ({
  longitude,
  latitude,
}: {
  longitude?: string;
  latitude?: string;
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Check if coordinates exist
    if (!longitude || !latitude) {
      setMapError("Coordinates not available");
      return;
    }

    try {
      // Parse coordinates and validate them
      const lng = parseFloat(longitude);
      const lat = parseFloat(latitude);

      // Verify coordinates are valid numbers
      if (isNaN(lng) || isNaN(lat)) {
        setMapError("Invalid coordinates format");
        return;
      }

      // Validate longitude is between -180 and 180
      if (lng < -180 || lng > 180) {
        setMapError("Invalid longitude: must be between -180 and 180");
        return;
      }

      // Validate latitude is between -90 and 90
      if (lat < -90 || lat > 90) {
        setMapError("Invalid latitude: must be between -90 and 90");
        return;
      }

      // Initialize map with valid coordinates
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [lng, lat],
        zoom: 15,
      });

      // Add navigation control
      map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

      // Add marker at land location
      new mapboxgl.Marker().setLngLat([lng, lat]).addTo(map.current);
    } catch (err) {
      console.error("Error initializing map:", err);
      setMapError("Failed to initialize map");
    }

    // Clean up on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [longitude, latitude]); // Only re-run if coordinates change

  // If there's an error, show it
  if (mapError) {
    return (
      <div className="w-full h-[300px] bg-gray-100 flex items-center justify-center text-gray-500">
        {mapError}
      </div>
    );
  }

  // If coordinates are missing, show message
  if (!longitude || !latitude) {
    return (
      <div className="w-full h-[300px] bg-gray-100 flex items-center justify-center text-gray-500">
        Location coordinates not available
      </div>
    );
  }

  return <div ref={mapContainer} className="w-full h-[300px]" />;
};

const LandDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  // Extract the numeric ID from either a numeric ID or a slug-based ID (title-123 format)
  const landId = id
    ? id.includes("-")
      ? parseInt(id.split("-").pop() || "")
      : parseInt(id)
    : undefined;

  const { land, loading, error } = useLandDetail(landId);
  const [isVisible, setIsVisible] = useState(false);

  // Use CSS transitions instead of GSAP for better performance
  useEffect(() => {
    if (land && !loading) {
      // Apply animation on next frame to ensure smooth rendering
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }

    return () => {
      setIsVisible(false);
    };
  }, [land, loading]);

  // Generate dynamic meta description based on land details
  const getMetaDescription = () => {
    if (!land) return metaData.landDetails.description;

    return `${land.title} - ${formatPrice(+land.price)}. ${land.area.value} ${land.area.unit} land for sale in ${land.location.suburb}. ${land.status} lot ${land.lot_number}.`;
  };

  // Generate dynamic meta title based on land details
  const getMetaTitle = () => {
    if (!land) return metaData.landDetails.title;

    return `${land.title} | ${land.area.value} ${land.area.unit} Land For Sale in ${land.location.suburb} | Timber Place`;
  };

  // Generate dynamic keywords based on land details
  const getMetaKeywords = () => {
    if (!land) return metaData.landDetails.keywords?.split(", ") || [];

    const baseKeywords = metaData.landDetails.keywords?.split(", ") || [];
    const dynamicKeywords = [
      `${land.area.value} ${land.area.unit} land`,
      land.location.suburb,
      land.location.address,
      `${land.status} land`,
      `${formatPrice(+land.price)} property`,
    ];

    return [...baseKeywords, ...dynamicKeywords];
  };

  // Generate a URL-friendly slug for the land
  const getMetaSlug = () => {
    if (!land) return "land-for-sale/detail";

    // Create a slug from the title and ID
    const titleSlug = land.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with a single one
      .trim();

    return `land-for-sale/${titleSlug}-${land.id}`;
  };

  if (loading) {
    return (
      <>
        {generateMeta({
          title: metaData.landDetails.title,
          description: metaData.landDetails.description,
          keywords: metaData.landDetails.keywords?.split(", "),
          slug: "land-for-sale/detail",
        })}
        <LandDetailsSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        {generateMeta({
          title: metaData.landDetails.title,
          description: metaData.landDetails.description,
          keywords: metaData.landDetails.keywords?.split(", "),
          slug: "land-for-sale/detail",
        })}
        <div className="pt-[140px] px-[6%] text-center text-red-500">
          {t("landDetails.error")}
        </div>
      </>
    );
  }

  if (!land) {
    return (
      <>
        {generateMeta({
          title: metaData.landDetails.title,
          description: metaData.landDetails.description,
          keywords: metaData.landDetails.keywords?.split(", "),
          slug: "land-for-sale/detail",
        })}
        <div className="pt-[140px] px-[6%] text-center">
          {t("landDetails.notFound")}
        </div>
      </>
    );
  }

  return (
    <div className="pt-[140px] px-[6%] pb-12">
      {generateMeta({
        title: getMetaTitle(),
        description: getMetaDescription(),
        keywords: getMetaKeywords(),
        image: land.main_image_url,
        slug: getMetaSlug(),
      })}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Content section */}
        <div
          className={`lg:w-1/2 lg:order-1 order-2 transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
          style={{ transitionDelay: "100ms" }}
        >
          <h1
            className={`text-6xl font-title italic font-bold text-[#333] mb-4 transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
            style={{ transitionDelay: "150ms" }}
          >
            {land.title}
          </h1>

          <div
            className={`flex items-center gap-4 mb-6 transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
            style={{ transitionDelay: "200ms" }}
          >
            <span className="text-3xl font-bold text-green-600">
              {formatPrice(+land.price)}
            </span>
          </div>

          <div
            className={`flex flex-wrap gap-4 mb-6 transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
            style={{ transitionDelay: "250ms" }}
          >
            <div className="border px-4 py-2 rounded-md">
              <span className="text-gray-500 text-sm font-medium">
                {t("landDetails.lotNumber")}
              </span>
              <p className="font-semibold">{land.lot_number}</p>
            </div>
            <div className="border px-4 py-2 rounded-md">
              <span className="text-gray-500 text-sm font-medium">
                {t("landDetails.location")}
              </span>
              <p className="font-semibold">{land.location.suburb}</p>
            </div>
          </div>

          <div
            className={`text-gray-700 mb-8 transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
            style={{ transitionDelay: "300ms" }}
            dangerouslySetInnerHTML={{ __html: land.description }}
          ></div>

          <div
            className={`grid grid-cols-2 gap-4 mb-8 transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
            style={{ transitionDelay: "350ms" }}
          >
            <div className="border p-4">
              <p className="text-gray-500 text-sm">{t("landDetails.status")}</p>
              <p className="font-medium">{land.status}</p>
            </div>
            <div className="border p-4">
              <p className="text-gray-500 text-sm">
                {t("landDetails.address")}
              </p>
              <p className="font-medium">{land.location.address}</p>
            </div>
            <div className="border p-4">
              <p className="text-gray-500 text-sm">{t("landDetails.area")}</p>
              <p className="font-medium">
                {land.area.value} {land.area.unit}
              </p>
            </div>
            <div className="border p-4">
              <p className="text-gray-500 text-sm">
                {t("landDetails.frontage")}
              </p>
              <p className="font-medium">
                {land.frontage.value} {land.frontage.unit}
              </p>
            </div>
            <div className="border p-4">
              <p className="text-gray-500 text-sm">{t("landDetails.stage")}</p>
              <p className="font-medium">{land.stage}</p>
            </div>
            <div className="border p-4">
              <p className="text-gray-500 text-sm">
                {t("landDetails.lastUpdated")}
              </p>
              <p className="font-medium">
                {new Date(land.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Map component - added before the enquire button */}
          <div
            className={`mb-8 transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
            style={{ transitionDelay: "375ms" }}
          >
            <h3 className="text-xl font-title italic font-bold mb-4">
              {t("landDetails.location")}
            </h3>
            <LocationMap
              longitude={land.location.longitude}
              latitude={land.location.latitude}
            />
          </div>

          <button
            className={`bg-primary text-white px-8 py-3 font-medium hover:bg-gray-800 font-title italic font-bold transition-all duration-700 ease-out ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
            style={{ transitionDelay: "400ms" }}
            onClick={() => store.dispatch(showEnquireModal())}
          >
            {t("landDetails.enquireNow")}
          </button>
        </div>

        {/* Image section */}
        <div
          className={`lg:w-1/2 lg:order-2 order-1 mb-6 lg:mb-0 lg:sticky lg:top-[120px] lg:self-start transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <ImageViewer
            images={[land.main_image_url, ...land.slider_image_urls]}
          />
        </div>
      </div>
    </div>
  );
};

export default LandDetails;
